import { DOCUMENT } from '@angular/common';
import { Injectable, Signal, computed, effect, inject, signal } from '@angular/core';

/** The two themes the app offers. */
export type Theme = 'light' | 'dark';

/** localStorage key holding an explicit choice. Absent means the default below. */
const THEME_STORAGE_KEY = 'jmeterTheme';

/** What the app shows before anyone touches the toggle. */
const DEFAULT_THEME: Theme = 'light';

/**
 * Which theme the app is showing.
 *
 * Light is the default: the app opens light for everyone, whatever the machine's
 * `prefers-color-scheme` says, so the page people land on matches the rest of the LTL apps. Dark is
 * opt-in through the toolbar toggle, and once chosen it is remembered across visits.
 *
 * The `.dark` class goes on `<html>` rather than on a component, because `styles.css` redefines the
 * design tokens under `.dark` and the page background lives on the same element.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);

  /** The explicit choice, or null while the light default is in charge. */
  private readonly chosen = signal<Theme | null>(this.read());

  readonly theme: Signal<Theme> = computed(() => this.chosen() ?? DEFAULT_THEME);

  readonly isDark: Signal<boolean> = computed(() => this.theme() === 'dark');

  constructor() {
    effect(() => {
      this.document.documentElement.classList.toggle('dark', this.isDark());
    });
  }

  set(theme: Theme): void {
    this.chosen.set(theme);

    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Losing the preference on the next visit is not worth failing a click over.
    }
  }

  toggle(): void {
    this.set(this.isDark() ? 'light' : 'dark');
  }

  private read(): Theme | null {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      return stored === 'light' || stored === 'dark' ? stored : null;
    } catch {
      return null;
    }
  }
}
